import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/cabins', label: 'Cabin Bookings' },
    { 
      path: '/island-adventure', 
      label: 'Island Tours & Adventures',
      hasDropdown: true,
      dropdownItems: [
        { path: '/island-adventure#hot-springs', label: 'Hot Springs Tour' },
        { path: '/island-adventure#taxi-services', label: 'Taxi Services' }
      ]
    },
    { path: '/charters', label: 'Charters' },
    { path: '/gallery', label: 'Gallery' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white shadow-lg fixed w-full top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/qruowtpm_strawberryislandlogopng.png"
              alt="Strawberry Island Logo"
              className="h-16 w-auto"
            />
            <div className="flex flex-col">
              <span className="text-2xl brand-script" style={{ color: '#20B2AA' }}>
                Strawberry Island
              </span>
              <span className="text-xs brand-charters tracking-wider" style={{ color: '#FF69B4', fontSize: '0.65rem' }}>
                CHARTERS | TOURS | STAYS
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div 
                  key={link.path}
                  className="relative"
                  onMouseEnter={() => setIsDropdownOpen(true)}
                  onMouseLeave={() => setIsDropdownOpen(false)}
                >
                  <Link
                    to={link.path}
                    className={`text-lg font-medium transition-colors duration-200 flex items-center gap-1 ${
                      isActive(link.path)
                        ? 'border-b-2 pb-1'
                        : 'hover:opacity-70'
                    }`}
                    style={{
                      color: isActive(link.path) ? '#FF69B4' : '#20B2AA',
                      borderColor: '#FF69B4',
                    }}
                  >
                    {link.label}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </Link>
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-white shadow-lg rounded-md py-2 z-50">
                      {link.dropdownItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="block px-4 py-2 text-base hover:bg-gray-100 transition-colors"
                          style={{ color: '#20B2AA' }}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-lg font-medium transition-colors duration-200 ${
                    isActive(link.path)
                      ? 'border-b-2 pb-1'
                      : 'hover:opacity-70'
                  }`}
                  style={{
                    color: isActive(link.path) ? '#FF69B4' : '#20B2AA',
                    borderColor: '#FF69B4',
                  }}
                >
                  {link.label}
                </Link>
              )
            ))}
            
            {/* Social Media Icons */}
            <div className="flex items-center space-x-3 ml-4">
              <a
                href="https://www.facebook.com/strawberryislandcharters"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-70 transition-opacity"
                data-testid="facebook-link"
              >
                <svg className="w-6 h-6" style={{ color: '#20B2AA' }} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/strawberry_island_charters/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-70 transition-opacity"
                data-testid="instagram-link"
              >
                <svg className="w-6 h-6" style={{ color: '#20B2AA' }} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-md"
            style={{ color: '#20B2AA' }}
            data-testid="mobile-menu-button"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden pb-4" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <div key={link.path}>
                <Link
                  to={link.path}
                  className="block py-2 px-4 text-lg font-medium"
                  style={{
                    color: isActive(link.path) ? '#FF69B4' : '#20B2AA',
                    backgroundColor: isActive(link.path) ? '#FFF8DC' : 'transparent',
                  }}
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </Link>
                {link.hasDropdown && link.dropdownItems && (
                  <div className="pl-8">
                    {link.dropdownItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="block py-2 px-4 text-base"
                        style={{ color: '#20B2AA' }}
                        onClick={() => setIsOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;